import {
    EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    normalizeDataLayer,
    TagDataLayerPartial,
} from '@axa-fr-axion/tag';
import { ProfilClient } from '@axa-fr-axion/user-journey-services';
import { AppOptions, JourneyData } from 'App.interface';
import { GenderOptions } from 'features/contact/identity/gender';
import { NavigationPath, StepNavigationPath } from 'interfaces';
import { getTagData } from './Tag.data';
import { TagCivility } from './Tag.interface';
import hashSha from 'utils/hashSha';
import { Preset } from 'presets';
import { calculateAge } from 'App.utils';

export const SEND_TAG_EVENT_NAME = 'sendTag';
export const EXCLUDED_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'code_du_produit',
    'protection_activite',
    'protection_dirigeant',
    'protection_salaries',
    'hashnpc',
    'auto_bonus_malus',
];
export const KEEPING_SPACE_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'form_contact_prenom_hash',
    'form_contact_nom_hash',
];

export const getHashedDataLayer = async (data: JourneyData, options?: AppOptions): Promise<TagDataLayerPartial> => {
    const fiscalJourneyDataLayer = await journeyDataToDataLayer(data, options);
    const normalizedDataLayer = normalizeDataLayer(
        fiscalJourneyDataLayer,
        EXCLUDED_KEY_TO_NORMALIZE,
        KEEPING_SPACE_KEY_TO_NORMALIZE
    );
    return normalizedDataLayer;
};

export const isEligibleMPE = (data: Partial<JourneyData>, preset?: Preset) => {
    return '';
};

const journeyDataToDataLayer = async (data: JourneyData, options?: AppOptions): Promise<TagDataLayerPartial> => {
    const {
        identity,
        homeAddress,
        folder,
    } = data;

    const nomHash = await hashSha(identity.lastname?.toLowerCase());
    const prenomHash = await hashSha(identity.firstname?.toLowerCase());
    const codepostalHash = await hashSha(homeAddress.postalCode?.toLowerCase());

    const hashnpc = (await hashSha(nomHash + prenomHash + codepostalHash, false))?.toUpperCase();

    return {
        form_contact_ville: homeAddress.city,
        type_hors_cible: '',
        lead_canal_traitement: '',

        statut_client: folder.personne?.profilClient?.toLowerCase() as Lowercase<ProfilClient>,
        code_du_produit: 'auto',
        form_numero_dossier: folder.lead?.id,
        form_numero_dossier_hash: folder.lead?.hash,
        auto_marqueSelect: data.vehicle?.brand,
        auto_modelSelect: data.vehicle?.model,
        auto_mise_en_circulation: data.vehicle?.circulationDate?.year,
        auto_carrosserieSelect: data.vehicle?.bodyType,
        auto_motorisationSelect: data.vehicle?.motorizationType,
        auto_bonus_malus: data.bonusPenaltyCoefficient,
        form_contact_anneepermis: data.licence?.year,
        form_contact_moispermis: data.licence?.month,
        form_age: calculateAge(data.birthDate || '') || '',
        form_contact_datenaissance: data.birthDate?.replaceAll('/', '_').toUpperCase(),
        form_contact_datenaissance_hash: await hashSha(data.birthDate?.replaceAll('/', '_').toLowerCase()),
        form_contact_codepostal: homeAddress.postalCode,
        form_contact_codepostal_hash: codepostalHash,
        form_contact_civilite: toTagCivility(identity.gender),
        form_contact_email_hash: await hashSha(identity.email?.toLowerCase()),
        form_contact_email_hash_2: await hashSha(identity.email?.toLowerCase(), false),
        form_contact_nom_hash: nomHash,
        form_contact_prenom_hash: prenomHash,
        form_contact_telephone_hash: await hashSha(identity.phone?.toLowerCase()),
        formule_recommandee: data.pricing?.recommended?.coverageOption.label,
        formule_selectionnee: data.pricing?.selected?.coverageOption.label,
        date_debut_contrat: data.insuranceDelay,
        hashnpc,
    };
};

export const shouldSendTagEventNavigation = (step: NavigationPath): step is StepNavigationPath =>
    Boolean(getTagData()[ step as StepNavigationPath ]);

export const toTagCivility = (gender?: GenderOptions): TagCivility | '' => {
    switch (gender) {
        case 'ms':
            return 'madame';
        case 'mr':
            return 'monsieur';
        default:
            return '';
    }
};

export const yesOrNo = (value = false) => {
    return value ? 'oui' : 'non';
};
